<template>
    <div class="root">
        <div class="header-container">
            <div
                id="table-header"
                :style="calculateStickyRowsTotalWidth()">
                <div
                    v-for="(column, index) in visibleColumns"
                    :key="index"
                    :style="calculateWidth(index)"
                    class="cell column"
                    @click="sort(column)">

                    <span> {{ column.label }}</span>
                    <Icon
                        :svg="arrowDownIcon"
                        :class="{'visible': isSorted(column.key),
                                 asc: isSortedAsc(column.key),
                                 desc: isSortedDesc(column.key) }"
                        class="sort-icon"/>
                </div>
                <div
                    class="cell"
                    style="width: 15px;"/>
            </div>
        </div>

        <div
            class="body-container"
            style="padding-right: 15px;"
            @scroll="handleScroll">
            <div
                id="table-body"
                :style="calculateTotalTableWidth()">
                <div
                    v-for="(column, index) in visibleColumns"
                    :key="index"
                    :style="calculateWidth(index)"
                    class="column">
                    <div
                        v-for="(value, rowIndex) in rows"
                        :key="rowIndex"
                        :class="['cell', { 'cell-red': shouldBeRed(value, column) }]"
                        v-html="getValueForColumn(value, column)"/>
                </div>
            </div>
        </div>

        <div class="footer-container">
            <div
                id="table-footer"
                :style="calculateStickyRowsTotalWidth()">
                <div
                    v-for="(column, index) in visibleColumns"
                    :key="index"
                    :style="calculateWidth(index)"
                    class="cell column"
                    v-html="getTotalsValue(column)"/>
                <div
                    class="cell"
                    style="width: 15px;"/>
            </div>
        </div>
    </div>

</template>

<script>

import ArrowDownIcon from 'voluum-frontend-icons/arrow-down.svg';
import Icon from './Icon';
const debounce = require('lodash/debounce');

const toArray = (arrayLikeObject) => Array.prototype.slice.call(arrayLikeObject);
const getColumnsWidthsBySelector = (parent, selector)  => {
    return toArray(parent.querySelectorAll(`${selector} .column`)).map(getColumnsWidth);
};
const getColumnsWidth = (column) => column.getBoundingClientRect().width;
const findLongest = (elements) => elements.sort((a, b) => a - b).pop();
const arraySum = (arr) => arr.reduce((previous, current) => previous + current, 0);

const WIDTH_AUTO = 'width: auto;';

const SORT_DIRECTIONS = {
    ASC: 'asc',
    DESC: 'desc'
};

const columnWidths = [];
const totalWidth = WIDTH_AUTO;
const scrollBarWidth = 15;

export default {
    name: 'Table',

    components: {
        Icon
    },

    props: {
        report: {
            type: Object,
            required: true
        },

        columns: {
            type: Array,
            required: true
        },
    },

    data: function () {
        return {
            columnWidths: columnWidths,
            totalWidth: totalWidth,
            scrollBarWidth: scrollBarWidth,
            arrowDownIcon: ArrowDownIcon,
            maxWidth: 0,
            strechColumnsToMaxWidth: false,
            counter: 0
        };
    },

    computed: {
        rows: function () {
            return this.report.rows;
        },

        totals: function () {
            return this.report.totals;
        },

        columnsVisibility: function () {
            return this.store.getters.columnsVisibilityMap;
        },

        visibleColumns: function () {
            return this.columns.filter(column => column.visible);
        }
    },

    watch: {
        columns: function () {
            this.renderTables();
        }
    },

    mounted: function () {
        this.renderTables();
        window.addEventListener('resize', this.handleResize);
    },

    beforeDestroy: function () {
        window.removeEventListener('resize', this.handleResize);
    },

    methods: {

        handleResize: debounce(function () {
            this.renderTables();
        }, 300),

        renderTables: function () {
            this.resetTableWidths();

            this.$nextTick(function () {
                this.calculateColumnWidths();
            });
        },

        calculateWidth: function (columnIndex) {
            if(this.strechColumnsToMaxWidth) {
                return this.toCSSWidth(this.ratios[columnIndex] * this.maxWidth);
            } else {
                if(this.columnWidths[columnIndex]) {
                    return this.toCSSWidth(this.columnWidths[columnIndex]);
                }
            }
        },

        calculateStickyRowsTotalWidth: function () {
            if(this.strechColumnsToMaxWidth) {
                return this.toCSSWidth(this.maxWidth);
            }

            return this.toCSSWidth(this.totalWidth + this.scrollBarWidth);
        },

        calculateTotalTableWidth: function () {
            if(this.strechColumnsToMaxWidth) {
                return this.toCSSWidth(this.maxWidth);
            }

            return this.toCSSWidth(this.totalWidth);
        },

        getTotalsValue: function (column) {
            const value = this.totals[column.key];

            if(value !== null && value !== undefined) {
                return this.getValueForColumn(this.totals, column);
            } else {
                return '';
            }
        },

        handleScroll: function (event) {
            const target = event.target;

            const headerContainer = this.$el.querySelector('.footer-container');
            const footerContainer = this.$el.querySelector('.header-container');

            headerContainer.scrollLeft = target.scrollLeft;
            footerContainer.scrollLeft = target.scrollLeft;
        },

        calculateColumnWidths: function () {
            const bodyContainer = this.$el.querySelector('.body-container');
            const maximumTableDimensions = bodyContainer.getBoundingClientRect();

            const columnsInTables = [
                getColumnsWidthsBySelector(this.$el, '#table-header'),
                getColumnsWidthsBySelector(this.$el, '#table-footer'),
                getColumnsWidthsBySelector(this.$el, '#table-body')
            ];

            const columnsAmount = this.visibleColumns.length;
            const columnWidths = [];

            for(let i = 0; i < columnsAmount; i++) {
                columnWidths[i] = findLongest([columnsInTables[0][i], columnsInTables[1][i], columnsInTables[2][i]]) || 0;
            }

            this.totalWidth = arraySum(columnWidths);
            this.columnWidths = columnWidths;

            this.ratios = this.columnWidths.map(c => c / this.totalWidth);

            this.maxWidth = maximumTableDimensions.width - scrollBarWidth;
            this.strechColumnsToMaxWidth = this.totalWidth < this.maxWidth;
        },

        resetTableWidths: function () {
            this.strechColumnsToMaxWidth = false;

            this.columnWidths = this.columns
                .map(() => 0);

            this.totalWidth = WIDTH_AUTO;
        },

        toCSSWidth: function (w) {
            return `width: ${w}px;`;
        },

        sort: function (column) {
            const sort = this.$store.state.sort;
            let sortDirection = sort.sortDirection;

            if(sort.sortKey === column.key) {
                sortDirection = sort.sortDirection === SORT_DIRECTIONS.ASC ? SORT_DIRECTIONS.DESC : SORT_DIRECTIONS.ASC;
            }

            this.$store.commit('setSort', { sortKey: column.key, sortDirection: sortDirection });
            this.$store.dispatch('loadReport');
        },

        isSorted: function (column) {
            return this.$store.state.sort.sortKey === column;
        },

        isSortedAsc: function () {
            return this.$store.state.sort.sortDirection === SORT_DIRECTIONS.ASC;
        },

        isSortedDesc: function () {
            return this.$store.state.sort.sortDirection === SORT_DIRECTIONS.DESC;
        },

        getValueForColumn: function (row, column) {
            const formatter = FORMATTERS[column.type];
            let value = row[column.key];

            if(formatter) {
                value = formatter(value, this.report.currency);
            }

            if(value !== null && value !== undefined) {
                return value;
            } else {
                return '&nbsp;';
            }
        },

        shouldBeRed: function (row, column) {
            const value = row[column.key];
            const isPercentageOrMonetary = column.type === 'percentage' || column.type === 'monetary';
            const isLessThanZero = typeof value === 'number' && value < 0;

            return isPercentageOrMonetary && isLessThanZero;
        }
    }
};

const monetaryFormatter = function (value, currency) {
    value = value || 0;
    if(value >= 0) {
        return `${currency} ${value}`;
    } else {
        return `(${currency} ${value})`;
    }
};

const DAYS = [null, 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const MONTHS = [null, 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const FORMATTERS = {
    'percentage': function (value) {
        value = value || 0;
        return `${value.toFixed(2)}%`;
    },
    'string': function (value) {
        return value || 'Unknown';
    },
    'integer-decimal-2': function (value) {
        value = value || 0;
        return value.toString().toFixed(2);
    },
    'integer': function (value) {
        value = value || 0;
        return value.toLocaleString('en-US');
    },
    'monetary': monetaryFormatter,
    'monetary-decimal-3': monetaryFormatter,
    'monetary-decimal-4': monetaryFormatter,
    'http-code': function (value) {
        return value || 'n/a';
    },
    'hour-of-day': function (value) {
        let hour = value.toString();
        if (hour.length == 1) {
            hour = '0' + hour;
        }
        return `${hour}:00 - ${hour}:59`;
    },
    'day-of-week': function (value) {
        return DAYS[value];
    },
    'month': function (value) {
        return MONTHS[value];
    },
    'yesno': function (value) {
        return value === true ? 'Yes' : 'No';
    },
    'site-id': function (value) {
        return value === 'UNKNOWN' ? 'Applications' : value;
    },
    'plainSiteId': function (value) {
        return value === 'UNKNOWN' ? 'Applications' : value;
    },
    'applications-bundle': function (value) {
        if (value === '') {
            return 'Unknown';
        }

        return value === 'UNKNOWN' ? 'Sites' : value;
    },
    'mobile-carrier': function (value) {
        return value || 'Other';
    },
    'publisher-name': function (value) {
        return value || 'Tracker Campaign';
    },
    'optimization-type': function (value) {
        return value ? 'Optimized' : 'Non-optimized';
    }
};
</script>

<style lang="less" scoped>

    @import "../styles/colors.less";
    @import "../styles/globals.less";

    @table-blue: #67708B;

    .root {
        width: 100%;
        height: 100%;
        overflow: hidden;

        position: relative;
    }

    #table-header,
    #table-body,
    #table-footer  {
        .cell {
            padding: 0 @padding;
            display: flex;
            height: @row-height;
            align-items: center;
        }
    }

    .column {
        flex-shrink: 0;
    }

    .column .cell:nth-child(even) {
        background-color: #f3f3f3;
    }

    #table-header .cell,
    #table-footer .cell {
        cursor: pointer;
        color: white;
        border-left: 1px solid @color-white;
    }

    #table-header .cell:first-of-type {
        border-left: 1px solid @table-blue;
    }

    #table-body .cell {
        border-left: 1px solid #e2e2e2;
    }

    .cell {
        box-sizing: border-box;
    }

    .cell-red {
        color: @color-red;
    }

    #table-body .cell, #table-header .cell {
        white-space: nowrap;
    }

    #table-body,
    #table-footer {
        .cell {
            text-align: left;
        }
    }

    .root,
    .header-container,
    .body-container,
    .footer-container {
        display: flex;
        flex-direction: column;
    }

    #table-header,
    #table-footer,
    #table-body,
    #table-header .cell {
        display: flex;
    }

    .header-container,
    .footer-container {
        background-color: @table-blue;
        overflow: hidden;
    }

    .body-container {
        flex: 1;
        overflow-x: auto;
        overflow-y: scroll;
    }

    .asc {
        transform: rotateX(180deg);
    }

    .sort-icon {
        .not-visible();
    }

    .visible.sort-icon {
        visibility: visible;
    }

    #table-header .cell:hover {
        background-color: #757D97;

        .visible.asc {
            visibility: visible;
            transform: rotateX(0deg);
        }

        .visible.desc {
            visibility: visible;
            transform: rotateX(180deg);
        }

        .sort-icon {
            visibility: visible;
        }
    }
</style>



// WEBPACK FOOTER //
// src/components/Table.vue